import * as React from "react";
import { Box, Button, Paper, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import Markdown from "react-markdown";
import { scrollToBookmark, scrollToParagraph } from "../../../utils/taskpane";
import { Citation } from "../utils/citationParser";
import { EditInfo } from "../utils/editTracker";
import remarkGfm from "remark-gfm";

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  edits?: EditInfo[];
  citations?: Citation[];
  model?: string;
  timestamp?: number;
}

interface ChatMessageBubbleProps {
  message: ChatMessage;
  messageIndex: number;
  onAcceptEdit: (messageIndex: number, editIndex: number) => void;
  onRejectEdit: (messageIndex: number, editIndex: number) => void;
  onAcceptAll: (messageIndex: number) => void;
  onRejectAll: (messageIndex: number) => void;
}

/** Navigate to a citation's location in the document. */
const navigateToCitation = (cit: Citation) => {
  if (cit.bookmarkName) {
    scrollToBookmark(cit.bookmarkName);
  } else {
    scrollToParagraph(cit.segmentId);
  }
};

const ChatMessageBubble: React.FC<ChatMessageBubbleProps> = ({
  message,
  messageIndex,
  onAcceptEdit,
  onRejectEdit,
  onAcceptAll,
  onRejectAll,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{ display: "flex", justifyContent: message.role === "user" ? "flex-end" : "flex-start" }}
    >
      <Paper
        variant={message.role === "user" ? "outlined" : "elevation"}
        elevation={0}
        className="jylo-bubble"
        sx={{
          px: 1.5,
          maxWidth: "100%",
          bgcolor: message.role === "user" ? "primary.light" : "transparent",
          borderColor: message.role === "user" ? "primary.light" : "transparent",
          borderRadius: 2,
          border: message.role === "assistant" ? "none" : undefined,
          "& table": {
            borderSpacing: 0,
            borderCollapse: "collapse",
            display: "block",
            marginTop: 0,
            marginBottom: 2,
          },
          "& tr": {
            backgroundColor: theme.palette.background.paper,
            borderTop: "1px solid",
            borderColor: theme.palette.divider,
          },
          "& td, & th": {
            padding: "6px 13px",
            border: "1px solid",
            borderColor: theme.palette.divider,
          },
          "& th": {
            fontWeight: 600,
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <Markdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({ node, href, children, ...props }) => {
              const citeMatch = href && /^#cite-(\d+)$/.exec(href);
              if (citeMatch) {
                const num = parseInt(citeMatch[1], 10);
                const cit = message.citations?.[num - 1];
                if (cit) {
                  return (
                    <Box
                      component="span"
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigateToCitation(cit);
                      }}
                      sx={{
                        display: "inline",
                        cursor: "pointer",
                        color: "primary.main",
                        fontWeight: 600,
                        fontSize: "0.95em",
                        verticalAlign: "super",
                        "&:hover": { textDecoration: "underline", color: "primary.dark" },
                      }}
                      title={cit.label}
                    >
                      {children}
                    </Box>
                  );
                }
              }
              return (
                <a href={href} {...props} onClick={(e: React.MouseEvent) => e.preventDefault()}>
                  {children}
                </a>
              );
            },
          }}
        >
          {message.content}
        </Markdown>

        {message.edits && message.edits.length > 0 && (
          <Stack spacing={0.5} mt={1}>
            {message.edits.filter((e) => e.status === "pending").length > 1 && (
              <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  onClick={() => onAcceptAll(messageIndex)}
                  sx={{ fontSize: 11, py: 0, px: 1, minWidth: 0 }}
                >
                  Accept All
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => onRejectAll(messageIndex)}
                  sx={{ fontSize: 11, py: 0, px: 1, minWidth: 0 }}
                >
                  Reject All
                </Button>
              </Box>
            )}
            {message.edits.map((edit, i) => (
              <Paper
                key={`edit-${i}`}
                variant="outlined"
                sx={{
                  p: 1,
                  bgcolor: edit.status === "rejected" ? "action.disabledBackground" : "primary.50",
                  borderColor:
                    edit.status === "accepted"
                      ? "primary.main"
                      : edit.status === "rejected"
                        ? "text.disabled"
                        : "primary.light",
                  opacity: edit.status === "rejected" ? 0.6 : 1,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                <EditIcon
                  sx={{
                    fontSize: 16,
                    color: edit.status === "rejected" ? "text.disabled" : "primary.main",
                    mt: 0.25,
                  }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    color={edit.status === "rejected" ? "text.disabled" : "primary.main"}
                  >
                    {edit.status === "accepted"
                      ? "Edit accepted"
                      : edit.status === "rejected"
                        ? "Edit rejected"
                        : "Proposed edit"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: 11,
                      textDecoration: edit.status === "rejected" ? "line-through" : "none",
                    }}
                  >
                    <span style={{ textDecoration: "line-through", color: "#d32f2f" }}>
                      {edit.find}
                    </span>
                    {" → "}
                    <span style={{ color: edit.status === "rejected" ? "#999" : "#2e7d32" }}>
                      {edit.replace}
                    </span>
                  </Typography>
                </Box>
                {edit.status === "pending" && edit.bookmarkName && (
                  <Box sx={{ display: "flex", gap: 0.25, flexShrink: 0, mt: 0.25 }}>
                    <Tooltip title="Accept Edit">
                      <CheckCircleOutlineIcon
                        onClick={() => onAcceptEdit(messageIndex, i)}
                        sx={{
                          fontSize: 20,
                          color: "success.main",
                          cursor: "pointer",
                          "&:hover": { color: "success.dark" },
                        }}
                        titleAccess="Accept Edit"
                      />
                    </Tooltip>
                    <Tooltip title="Reject Edit">
                      <HighlightOffIcon
                        onClick={() => onRejectEdit(messageIndex, i)}
                        sx={{
                          fontSize: 20,
                          color: "error.main",
                          cursor: "pointer",
                          "&:hover": { color: "error.dark" },
                        }}
                        titleAccess="Reject Edit"
                      />
                    </Tooltip>
                  </Box>
                )}
                {edit.status === "accepted" && (
                  <Typography
                    variant="caption"
                    color="primary.main"
                    sx={{ flexShrink: 0, mt: 0.25 }}
                  >
                    ✓
                  </Typography>
                )}
                {edit.status === "rejected" && (
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ flexShrink: 0, mt: 0.25 }}
                  >
                    ✗
                  </Typography>
                )}
              </Paper>
            ))}
          </Stack>
        )}

        {message.citations && message.citations.length > 0 && (
          <Stack spacing={0.25} mt={1} sx={{ borderTop: 1, borderColor: "divider", pt: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">
              References
            </Typography>
            {message.citations.map((cit, i) => (
              <Typography
                key={`cit-footer-${i}`}
                variant="caption"
                onClick={() => navigateToCitation(cit)}
                sx={{
                  cursor: "pointer",
                  color: "primary.main",
                  "&:hover": { textDecoration: "underline", color: "primary.dark" },
                }}
              >
                [{i + 1}] {cit.label}
              </Typography>
            ))}
          </Stack>
        )}

        {message.role === "assistant" && (message.model || message.timestamp) && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{
              display: "block",
              textAlign: "right",
              mt: 0.5,
              fontSize: 10,
              opacity: 0,
              transition: "opacity 0.2s",
              ".jylo-bubble:hover &": { opacity: 1 },
            }}
          >
            {[
              message.model,
              message.timestamp
                ? new Date(message.timestamp).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : undefined,
            ]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default ChatMessageBubble;
